// Auth page — login / signup / verify all share this component, swapped
// by the URL param. Layout mirrors Overleaf:
//
//   • signup → split: form card on the left, StudioMockup on the right
//   • login / verify → single centered form card (no mockup)
//
// Brand sits in a minimal top-left header (no nav, no sticky bar) so the
// form has the visual weight of the page.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { setToken, setStoredUser } from '../lib/auth';
import { Brand } from '../components/Brand';
import { LangToggle } from '../components/LangToggle';
import { PublicNavLinks } from '../components/PublicNavLinks';
import { StudioMockup } from '../components/StudioMockup';
import { useT } from '../lib/i18n';
import './Auth.css';

type Mode = 'login' | 'signup' | 'verify';

interface AuthResp { token: string; user: { id: string; email: string; plan: string } }

// Cloudflare Turnstile injects this onto window once the script loads.
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void; 'error-callback'?: () => void; 'expired-callback'?: () => void }) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export function Auth() {
  const t = useT();
  const { page } = useParams<{ page: string }>();
  const mode: Mode = (page === 'signup' ? 'signup' : page === 'verify' ? 'verify' : 'login');
  const [params] = useSearchParams();
  // 登录完回到原来要去的地方(RequireAuth / api.ts 的 401 都带 ?redirect=);没有就回首页(项目库)。
  // 只认站内路径 —— 「//evil.com」这种协议相对地址不能当跳转目标。
  const want = params.get('redirect') || '/';
  const redirect = want.startsWith('/') && !want.startsWith('//') ? want : '/';
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // ─── Turnstile widget + closed-beta invite gate (signup only) ──────
  // /config/public exposes two flags:
  //   - turnstile_site_key: when set, render the Turnstile widget + send
  //     the token on submit (captcha on)
  //   - beta_invite_required: when true, show the invite-code input +
  //     send `invite_code` field on submit. Internal testers receive
  //     the code out-of-band; public traffic instead clicks the
  //     "申请内测" CTA on Landing to join the waitlist.
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [betaRequired, setBetaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (mode !== 'signup') return;
    let cancelled = false;
    api<{ turnstile_site_key: string | null; beta_invite_required?: boolean }>(
      '/api/v1/config/public',
    )
      .then(r => {
        if (cancelled) return;
        setSiteKey(r.turnstile_site_key);
        setBetaRequired(!!r.beta_invite_required);
      })
      .catch(() => { /* leave both off → open signup */ });
    return () => { cancelled = true; };
  }, [mode]);

  useEffect(() => {
    if (mode !== 'signup' || !siteKey || !captchaRef.current) return;
    // Load Turnstile script once, then render the widget.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
    function mount() {
      if (!window.turnstile || !captchaRef.current) return;
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: siteKey!,
        callback: tok => setCaptchaToken(tok),
        'expired-callback': () => setCaptchaToken(null),
        'error-callback':   () => setCaptchaToken(null),
      });
    }
    if (existing && window.turnstile) {
      mount();
    } else if (!existing) {
      const s = document.createElement('script');
      s.src = TURNSTILE_SCRIPT;
      s.async = true; s.defer = true;
      s.onload = mount;
      document.head.appendChild(s);
    } else {
      existing.addEventListener('load', mount, { once: true });
    }
  }, [siteKey, mode]);

  function resetCaptcha() {
    setCaptchaToken(null);
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (mode === 'signup' && siteKey && !captchaToken) {
      setError(t('auth.error.captcha_required'));
      return;
    }
    setPending(true);
    try {
      if (mode === 'login') {
        const r = await api<AuthResp>('/api/v1/auth/login', {
          method: 'POST',
          body: { email, password },
        });
        setToken(r.token);
        setStoredUser(r.user);
        navigate(redirect, { replace: true });
      } else if (mode === 'signup') {
        await api('/api/v1/auth/signup', {
          method: 'POST',
          body: {
            email, password,
            cf_turnstile_token: captchaToken,
            invite_code: betaRequired ? inviteCode.trim() : undefined,
          },
        });
        setInfo(t('auth.signup_check_inbox'));
        navigate(`/auth/verify?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirect)}`);
      } else {
        const r = await api<AuthResp>('/api/v1/auth/verify', {
          method: 'POST',
          body: { email, code },
        });
        setToken(r.token);
        setStoredUser(r.user);
        navigate(redirect, { replace: true });
      }
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : '';
      // Translate the two structured 403 codes from /signup into local
      // copy so testers immediately understand it's invite-related.
      if (raw.includes('invite_code_required')) {
        setError(t('auth.error.invite_code_required'));
      } else if (raw.includes('invite_code_invalid')) {
        setError(t('auth.error.invite_code_invalid'));
      } else {
        setError(raw || t('auth.error.generic'));
      }
      if (mode === 'signup') resetCaptcha();
    } finally {
      setPending(false);
    }
  }

  const title = mode === 'login' ? t('auth.title.login')
              : mode === 'signup' ? t('auth.title.signup')
              : t('auth.title.verify');

  const isSplit = mode === 'signup';

  return (
    <div className="auth-page">
      <header className="auth-header">
        <Brand size={28} />
        <nav className="auth-header-nav"><PublicNavLinks /></nav>
        <div className="auth-header-right">
          <LangToggle />
        </div>
      </header>

      <main className={`auth-main${isSplit ? ' auth-main--split' : ''}`}>
        <div className="auth-form-col">
          <form className="auth-card" onSubmit={onSubmit}>
            <h1>{title}</h1>

            {/* 2026-10-15:谷歌 / 微信登录还没接(后端没有这两条路由),灰着的「即将上线」按钮下线 —— 不能用的不挂出来 */}

            <label htmlFor="auth-email">{t('auth.field.email')}</label>
            <input
              id="auth-email"
              type="email" value={email} required autoComplete="email"
              onChange={e => setEmail(e.target.value)} disabled={pending}
            />

            {mode !== 'verify' && (
              <>
                <label htmlFor="auth-password">{t('auth.field.password')}</label>
                <input
                  id="auth-password"
                  type="password" value={password} required minLength={6}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  onChange={e => setPassword(e.target.value)} disabled={pending}
                />
                {mode === 'signup' && (
                  <div className="auth-helper">{t('auth.password_helper')}</div>
                )}
                {mode === 'signup' && betaRequired && (
                  <>
                    <label htmlFor="auth-invite">{t('auth.field.invite_code')}</label>
                    <input
                      id="auth-invite"
                      type="text" value={inviteCode} required
                      autoComplete="off"
                      placeholder={t('auth.field.invite_code_placeholder')}
                      onChange={e => setInviteCode(e.target.value)}
                      disabled={pending}
                    />
                    <div className="auth-helper">{t('auth.invite_code_helper')}</div>
                  </>
                )}
              </>
            )}

            {mode === 'verify' && (
              <>
                <label htmlFor="auth-code">{t('auth.field.code')}</label>
                <input
                  id="auth-code"
                  value={code} required pattern="\d{6}" disabled={pending}
                  onChange={e => setCode(e.target.value)}
                />
              </>
            )}

            {mode === 'signup' && siteKey && (
              <div className="auth-captcha" ref={captchaRef} />
            )}

            {error && <div className="auth-msg err">{error}</div>}
            {info  && <div className="auth-msg info">{info}</div>}

            <button type="submit" className="btn btn-primary auth-submit" disabled={pending}>
              {pending ? t('auth.btn.working') : title}
            </button>

            {mode === 'signup' && (
              <p className="auth-legal muted small">
                {t('auth.legal_lead')}
                <Link to="/terms">{t('auth.legal_terms')}</Link>
                {t('auth.legal_and')}
                <Link to="/privacy">{t('auth.legal_privacy')}</Link>
                {t('auth.legal_end')}
              </p>
            )}

            <div className="auth-switch muted small">
              {mode === 'login' && (
                <>
                  {t('auth.switch.no_account')}{' '}
                  <Link to="/auth/signup">{t('auth.link.create_account')}</Link>
                </>
              )}
              {mode === 'signup' && (
                <>
                  {t('auth.switch.have_account')}{' '}
                  <Link to="/auth/login">{t('auth.link.signin')}</Link>
                </>
              )}
              {mode === 'verify' && (
                <Link to="/auth/login">{t('auth.link.back_signin')}</Link>
              )}
            </div>
          </form>
        </div>

        {isSplit && (
          <div className="auth-mockup-col" aria-hidden="true">
            <StudioMockup />
          </div>
        )}
      </main>
    </div>
  );
}

